import { makeStyles, tokens } from "@fluentui/react-components";
import type { FC } from "react";

// ---------------------------------------------------------------------------
// Payload shape — mirrors ApiFailureGate.build_prompt's JSON block
// ---------------------------------------------------------------------------

export interface ApiFailurePayload {
  endpoint: string;
  status_code: number | null;
  classification: "transient" | "auth" | "integrity" | "unknown";
  error: string;
  attempts: number;
  operation: string;
  section_number: string | null;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/** Extract the JSON block appended by ApiFailureGate.build_prompt. */
export function parseApiFailurePayload(question: string): ApiFailurePayload | null {
  const match = /\{[\s\S]*\}/u.exec(question);
  if (!match) return null;
  try {
    const obj = JSON.parse(match[0]);
    if (obj && typeof obj === "object" && typeof obj.endpoint === "string") {
      return obj as ApiFailurePayload;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  row: {
    display: "flex",
    gap: "6px",
    fontSize: "11px",
    alignItems: "flex-start",
  },
  key: {
    fontWeight: 700,
    color: tokens.colorNeutralForeground3,
    minWidth: "80px",
    flexShrink: 0,
  },
  value: {
    color: tokens.colorNeutralForeground1,
    wordBreak: "break-word",
  },
  errorBox: {
    padding: "6px 8px",
    background: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "3px",
    fontFamily: "monospace",
    fontSize: "10.5px",
    color: tokens.colorStatusDangerForeground1,
    whiteSpace: "pre-wrap",
    overflowY: "auto",
    maxHeight: "100px",
  },
  authWarning: {
    fontSize: "11px",
    fontWeight: 600,
    color: tokens.colorStatusDangerForeground1,
  },
});

const CLASSIFICATION_LABELS: Record<ApiFailurePayload["classification"], string> = {
  transient: "Transient (network/timeout)",
  auth: "Authentication failure",
  integrity: "Data not found / malformed",
  unknown: "Unknown",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface ApiFailureViewProps {
  payload: ApiFailurePayload;
}

const ApiFailureView: FC<ApiFailureViewProps> = ({ payload }) => {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      {payload.classification === "auth" && (
        <div className={styles.authWarning}>⚠ Re-login required — Continue is not safe for auth failures.</div>
      )}
      <div className={styles.row}>
        <span className={styles.key}>Endpoint</span>
        <span className={styles.value}>{payload.endpoint}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.key}>Status</span>
        <span className={styles.value}>
          {payload.status_code === null ? "no response" : String(payload.status_code)}
        </span>
      </div>
      <div className={styles.row}>
        <span className={styles.key}>Class</span>
        <span className={styles.value}>{CLASSIFICATION_LABELS[payload.classification] ?? payload.classification}</span>
      </div>
      {payload.attempts > 1 && (
        <div className={styles.row}>
          <span className={styles.key}>Attempts</span>
          <span className={styles.value}>{payload.attempts}</span>
        </div>
      )}
      {payload.operation && (
        <div className={styles.row}>
          <span className={styles.key}>Operation</span>
          <span className={styles.value}>{payload.operation}</span>
        </div>
      )}
      {payload.section_number && (
        <div className={styles.row}>
          <span className={styles.key}>Section</span>
          <span className={styles.value}>§{payload.section_number}</span>
        </div>
      )}
      {payload.error && <div className={styles.errorBox}>{payload.error}</div>}
    </div>
  );
};

export default ApiFailureView;
